import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within an AudioProvider');
    }
    return context;
};

export const AudioProvider = ({ children }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [hasInteracted, setHasInteracted] = useState(false);
    const audioRef = useRef(null);

    // User-provided music URL
    const audioUrl = 'https://customer-assets.emergentagent.com/job_6e8961e0-6eb8-4537-88a3-6700d06bf14d/artifacts/ohexvz4z_Relaxing%20music%20Relieves%20stress%2C%20Anxiety%20and%20Depression%20%F0%9F%8C%BF%20Heals%20the%20Mind%2C%20body%20and%20Soul%20-%20Deep%20Sleep%20%5BI3OJUwILelU%5D.mp3';

    useEffect(() => {
        // Create audio element
        audioRef.current = new Audio(audioUrl);
        audioRef.current.loop = true;
        audioRef.current.volume = 0.3;
        audioRef.current.muted = true;

        // Try to auto-play muted
        const playMuted = async () => {
            try {
                await audioRef.current.play();
                setIsPlaying(true);
            } catch (error) {
                console.log('Auto-play prevented:', error);
            }
        };

        playMuted();

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const toggleMute = () => {
        if (audioRef.current) {
            if (!hasInteracted) {
                setHasInteracted(true);
            }
            
            if (isMuted) {
                audioRef.current.muted = false;
                if (!isPlaying) {
                    audioRef.current.play().then(() => {
                        setIsPlaying(true);
                    }).catch(console.error);
                }
            } else {
                audioRef.current.muted = true;
            }
            setIsMuted(!isMuted);
        }
    };

    const play = async () => {
        if (audioRef.current && !isPlaying) {
            try {
                await audioRef.current.play();
                setIsPlaying(true);
            } catch (error) {
                console.error('Play failed:', error);
            }
        }
    };

    const pause = () => {
        if (audioRef.current && isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const setVolume = (volume) => {
        if (audioRef.current) {
            audioRef.current.volume = Math.max(0, Math.min(1, volume));
        }
    };

    return (
        <AudioContext.Provider value={{
            isPlaying,
            isMuted,
            hasInteracted,
            toggleMute,
            play,
            pause,
            setVolume
        }}>
            {children}
        </AudioContext.Provider>
    );
};
